import React, { useEffect, useMemo, useState } from 'react';
import {
    AutoComplete,
    Button,
    Form,
    Input,
    Modal,
    Select,
    Spin,
    Typography,
    message,
} from 'antd';
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    GlobalOutlined,
    MailOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import ModernAuthLayout from '../components/Auth/ModernAuthLayout';
import { BrandLogoIcon } from '../components/BrandLogo';
import {
    useGetCharterConfigQuery,
    useGetQualificationConfigQuery,
    useSubmitCharterApplicationMutation,
} from '../services/nestjs-base-api';
import { ISubmitCharterApplication } from '../services/nestjs-base-api/types';
import { resolveByCategory } from '../utils/qualificationResolver';
import { normalizeWebsiteUrl, isValidWebsiteUrl } from '../utils/websiteUrl';

const { Text, Title } = Typography;
const { TextArea } = Input;

// Charter Cohort (SYR-106). The public application that replaces self-serve
// signup while the intake is open.
//
// It creates NOTHING. No account, organisation, brand, survey, subscription or
// trial sits behind this submit — only a row on the Charter waitlist and an
// event a platform admin can subscribe to. An application is not a signup, and
// that distinction is the reason the cohort exists.
//
// Stepped rather than one long form, matching the signup qualification flow:
// eleven fields on one page is a wall, and every screen here fits without
// scrolling.
//
// Answers are accumulated in `answers` as each step is cleared, NOT read from
// the Form store at submit. Only the current step's Form.Items are mounted, so
// validateFields() resolves with just those fields and the payload would
// otherwise carry the last screen and nothing else. That failure is silent:
// the flow looks right the whole way through and posts almost nothing.

const STEP_TERMS = 0;
const STEP_BRAND = 1;
const STEP_SELL = 2;
// The reach questions are split across two screens rather than one. Four
// selects plus the reachable-list hint runs 260px past an 800px laptop
// viewport, and a step that scrolls is the thing this flow exists to avoid.
const STEP_REACH = 3;
const STEP_LIST = 4;
const STEP_QUESTION = 5;
const STEP_ABOUT = 6;
// Contact details are two screens, not one. The shared auth card is 843px
// against a 736px budget at an 800px viewport once its own padding, margins
// and footer are counted, which fits two fields and not three. Splitting here
// was cheaper and safer than reworking a layout sign-in and signup also use.
const STEP_ROLE = 7;
const LAST_STEP = STEP_ROLE;

// Validated when leaving each step, so a mistake surfaces where it was made
// rather than at submit, three screens later.
const FIELDS_BY_STEP: Record<number, string[]> = {
    [STEP_TERMS]: [],
    [STEP_BRAND]: ['brandName', 'websiteUrl'],
    [STEP_SELL]: ['productCategory', 'businessModel'],
    [STEP_REACH]: ['emailPlatform', 'customerCountBand'],
    [STEP_LIST]: ['reachableListBand', 'canSendWithin30Days'],
    [STEP_QUESTION]: ['unansweredQuestion'],
    [STEP_ABOUT]: ['contactName', 'email'],
    [STEP_ROLE]: ['roleAtBrand'],
};

const CharterPage: React.FunctionComponent = () => {
    const intl = useIntl();
    const [form] = Form.useForm();
    const [isMobile, setIsMobile] = useState(false);
    const [step, setStep] = useState<number>(STEP_TERMS);
    const [submitted, setSubmitted] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const { data: charterConfig, isLoading: isLoadingCharter } = useGetCharterConfigQuery();
    const { data: qualificationConfig, isLoading: isLoadingQualification } = useGetQualificationConfigQuery();
    const [submitApplication, submitResult] = useSubmitCharterApplicationMutation();

    const [productCategory, setProductCategory] = useState<string | undefined>();
    const [answers, setAnswers] = useState<Record<string, any>>({});

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Each step is its own screen, so return to the top when one changes, and
    // repopulate the newly mounted fields from the accumulated answers so Back
    // shows what was typed rather than an empty form.
    useEffect(() => {
        window.scrollTo(0, 0);
        form.setFieldsValue(answers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // The business-model follow-up is asked only when the category is genuinely
    // ambiguous — the same rule the signup quiz applies. Unlike signup, an
    // unsupported answer never routes anywhere: every application lands as
    // pending and Steve decides.
    const modelOptions = useMemo(() => {
        if (!qualificationConfig || !productCategory) return null;
        const byCategory = resolveByCategory(qualificationConfig, productCategory);
        return byCategory.needsModel ? byCategory.eligibleModels ?? [] : null;
    }, [qualificationConfig, productCategory]);

    const inputStyle: React.CSSProperties = {
        height: isMobile ? '52px' : '48px',
        borderRadius: '12px',
        border: '2px solid #e5e7eb',
        fontSize: '16px',
    };

    const primaryButtonStyle: React.CSSProperties = {
        height: isMobile ? '52px' : '48px',
        borderRadius: '12px',
        fontSize: '16px',
        fontWeight: 600,
    };

    const goNext = async () => {
        try {
            const fields = FIELDS_BY_STEP[step].filter(
                (f) => f !== 'businessModel' || !!modelOptions,
            );
            if (fields.length) {
                const values = await form.validateFields(fields);
                setAnswers((prev) => ({ ...prev, ...values }));
            }
            setStep((s) => Math.min(s + 1, LAST_STEP));
        } catch {
            // Antd has already marked the offending field; nothing to add.
        }
    };

    const goBack = () => setStep((s) => Math.max(s - 1, STEP_TERMS));

    const handleSubmit = async () => {
        let values: any;
        try {
            // Only the final step is mounted, so this validates and returns just
            // those fields. Everything earlier comes from `answers`, which each
            // cleared step contributed to.
            const lastStepValues = await form.validateFields(FIELDS_BY_STEP[LAST_STEP]);
            values = { ...answers, ...lastStepValues, companyWebsite: form.getFieldValue('companyWebsite') };
        } catch {
            return;
        }

        const payload: ISubmitCharterApplication = {
            brandName: values.brandName?.trim(),
            websiteUrl: normalizeWebsiteUrl(values.websiteUrl || ''),
            contactName: values.contactName?.trim(),
            email: values.email?.trim(),
            roleAtBrand: values.roleAtBrand?.trim(),
            productCategory: values.productCategory,
            businessModel: values.businessModel,
            emailPlatform: values.emailPlatform?.trim() || undefined,
            customerCountBand: values.customerCountBand,
            reachableListBand: values.reachableListBand,
            canSendWithin30Days: values.canSendWithin30Days,
            unansweredQuestion: values.unansweredQuestion?.trim(),
            companyWebsite: values.companyWebsite,
        };

        try {
            await submitApplication({ payload }).unwrap();
            setSubmitted(true);
            window.scrollTo(0, 0);
        } catch {
            messageApi.open({
                type: 'error',
                content: intl.formatMessage({ id: 'charter.form.error' }),
                duration: 6,
                icon: <ExclamationCircleOutlined />,
            });
        }
    };

    const charterLeftContent = (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '2rem' }}>
                <BrandLogoIcon size={48} />
                <span style={{ fontSize: '32px', fontWeight: '700' }}>
                    <span style={{ color: 'white' }}>BrandScience</span>
                    <span style={{ color: 'rgba(255,255,255,0.6)' }}>.ai</span>
                </span>
            </div>
            <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', lineHeight: '1.2' }}>
                {intl.formatMessage({ id: 'charter.hero.heading' })}
            </h1>
            <p style={{ fontSize: '1.25rem', opacity: 0.9, lineHeight: '1.6' }}>
                {intl.formatMessage({ id: 'charter.hero.description' })}
            </p>
        </div>
    );

    const stepHeader = (titleId: string, descriptionId: string) => (
        <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
            <Title level={2} style={{ color: '#1f2937', fontWeight: 700, marginBottom: '0.75rem', fontSize: isMobile ? '24px' : '28px' }}>
                {intl.formatMessage({ id: titleId })}
            </Title>
            <Text style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '15px', lineHeight: 1.6, display: 'block' }}>
                {intl.formatMessage({ id: descriptionId })}
            </Text>
        </div>
    );

    // Quiet dots rather than an antd Steps bar, which is too much furniture for
    // a narrow card.
    const progressDots = (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: '1rem' }}>
            {[STEP_TERMS, STEP_BRAND, STEP_SELL, STEP_REACH, STEP_LIST, STEP_QUESTION, STEP_ABOUT, STEP_ROLE].map((s) => (
                <span
                    key={s}
                    style={{
                        width: s === step ? 20 : 6,
                        height: 6,
                        borderRadius: 3,
                        background: s === step ? '#F59E0B' : s < step ? '#fcd34d' : '#e5e7eb',
                        transition: 'width 0.2s ease, background 0.2s ease',
                    }}
                />
            ))}
        </div>
    );

    const backButton = step > STEP_TERMS && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '0.5rem' }}>
            <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={goBack} style={{ color: '#6b7280' }}>
                {intl.formatMessage({ id: 'charter.step.back' })}
            </Button>
        </div>
    );

    // The terms stay one click away from every later step (rendered in the
    // secondary row below). They were shown and acknowledged, not buried, and
    // nobody should abandon the form to re-read what they agreed to look at.

    const isLoading = isLoadingCharter || isLoadingQualification;

    if (isLoading) {
        return (
            <ModernAuthLayout leftContent={charterLeftContent}>
                <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
                    <Spin size="large" />
                </div>
            </ModernAuthLayout>
        );
    }

    if (submitted) {
        return (
            <ModernAuthLayout leftContent={charterLeftContent}>
                <div style={{ textAlign: 'center', padding: isMobile ? '2rem 1rem' : '3rem 2rem' }}>
                    <div style={{ width: '72px', height: '72px', background: '#dcfce7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                        <CheckCircleOutlined style={{ fontSize: '36px', color: '#16a34a' }} />
                    </div>
                    <div className="charter-markdown" style={{ textAlign: 'left' }}>
                        <ReactMarkdown>{charterConfig?.confirmationMarkdown || ''}</ReactMarkdown>
                    </div>
                    <div style={{ marginTop: '2rem' }}>
                        <Text type="secondary">
                            {intl.formatMessage({ id: 'charter.confirmation.existingAccount' })}{' '}
                            <Link to="/sign-in">{intl.formatMessage({ id: 'charter.confirmation.signIn' })}</Link>
                        </Text>
                    </div>
                </div>
            </ModernAuthLayout>
        );
    }

    return (
        <ModernAuthLayout leftContent={charterLeftContent}>
            {contextHolder}
            {backButton}
            {progressDots}

            <Form form={form} layout="vertical" requiredMark={false} onFinish={handleSubmit}>
                {/* Honeypot. Always mounted so a bot filling the whole form is
                    caught whichever step it submits from. Off-screen rather than
                    display:none because some bots skip hidden fields, and
                    aria-hidden plus tabIndex keep it away from screen readers
                    and the keyboard. */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, overflow: 'hidden' }}>
                    <Form.Item name="companyWebsite" label="Company website">
                        <Input autoComplete="off" tabIndex={-1} />
                    </Form.Item>
                </div>

                {step === STEP_TERMS && (
                    <div>
                        {/* The intro and the terms share one scrollable box, so
                            the acknowledge button stays on screen no matter how
                            long Steve's copy grows. Seven commercial terms will
                            not shrink to fit a laptop, and a page that scrolls
                            to reach the button is the thing this flow replaced. */}
                        <div
                            className="charter-markdown"
                            style={{
                                background: '#f9fafb',
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                padding: isMobile ? '1rem' : '1.25rem 1.5rem',
                                marginBottom: '1.25rem',
                                maxHeight: isMobile ? '44vh' : 'min(48vh, 420px)',
                                overflowY: 'auto',
                            }}
                        >
                            {charterConfig?.introMarkdown && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <ReactMarkdown>{charterConfig.introMarkdown}</ReactMarkdown>
                                </div>
                            )}
                            <ReactMarkdown>{charterConfig?.termsMarkdown || ''}</ReactMarkdown>
                        </div>
                        {/* An acknowledgement, not a Next. The point of putting
                            the terms first is that they are read before the call,
                            so the action has to say what it means. */}
                        <Button type="primary" block size="large" style={primaryButtonStyle} onClick={goNext}>
                            {intl.formatMessage({ id: 'charter.step.termsAccept' })}
                        </Button>
                    </div>
                )}

                {step === STEP_BRAND && (
                    <div>
                        {stepHeader('charter.step.brand.title', 'charter.step.brand.description')}
                        <Form.Item
                            name="brandName"
                            label={intl.formatMessage({ id: 'charter.field.brandName' })}
                            rules={[{ required: true, message: intl.formatMessage({ id: 'charter.field.brandName.required' }) }]}
                        >
                            <Input style={inputStyle} placeholder={intl.formatMessage({ id: 'charter.field.brandName.placeholder' })} autoFocus />
                        </Form.Item>
                        <Form.Item
                            name="websiteUrl"
                            label={intl.formatMessage({ id: 'charter.field.websiteUrl' })}
                            rules={[
                                { required: true, message: intl.formatMessage({ id: 'charter.field.websiteUrl.required' }) },
                                {
                                    validator: (_rule, value: string) =>
                                        !value?.trim() || isValidWebsiteUrl(value) ? Promise.resolve() : Promise.reject(new Error()),
                                    message: intl.formatMessage({ id: 'charter.field.websiteUrl.invalid' }),
                                },
                            ]}
                        >
                            <Input prefix={<GlobalOutlined style={{ color: '#9ca3af' }} />} style={inputStyle} placeholder={intl.formatMessage({ id: 'charter.field.websiteUrl.placeholder' })} />
                        </Form.Item>
                        <Button type="primary" block size="large" style={primaryButtonStyle} onClick={goNext}>
                            {intl.formatMessage({ id: 'charter.step.continue' })}
                        </Button>
                    </div>
                )}

                {step === STEP_SELL && (
                    <div>
                        {stepHeader('charter.step.sell.title', 'charter.step.sell.description')}
                        <Form.Item
                            name="productCategory"
                            label={intl.formatMessage({ id: 'charter.field.productCategory' })}
                            rules={[{ required: true, message: intl.formatMessage({ id: 'charter.field.productCategory.required' }) }]}
                        >
                            <Select
                                size="large"
                                placeholder={intl.formatMessage({ id: 'charter.field.productCategory.placeholder' })}
                                onChange={(value: string) => {
                                    setProductCategory(value);
                                    form.setFieldValue('businessModel', undefined);
                                }}
                                options={(qualificationConfig?.productCategories || []).map((c) => ({ value: c.value, label: c.label }))}
                            />
                        </Form.Item>
                        {modelOptions && (
                            <Form.Item
                                name="businessModel"
                                label={intl.formatMessage({ id: 'charter.field.businessModel' })}
                                rules={[{ required: true, message: intl.formatMessage({ id: 'charter.field.businessModel.required' }) }]}
                            >
                                <Select
                                    size="large"
                                    placeholder={intl.formatMessage({ id: 'charter.field.businessModel.placeholder' })}
                                    options={modelOptions.map((m) => ({ value: m.value, label: m.label }))}
                                />
                            </Form.Item>
                        )}
                        <Button type="primary" block size="large" style={primaryButtonStyle} onClick={goNext}>
                            {intl.formatMessage({ id: 'charter.step.continue' })}
                        </Button>
                    </div>
                )}

                {step === STEP_REACH && (
                    <div>
                        {stepHeader('charter.step.reach.title', 'charter.step.reach.description')}
                        <Form.Item name="emailPlatform" label={intl.formatMessage({ id: 'charter.field.emailPlatform' })}>
                            <AutoComplete
                                size="large"
                                placeholder={intl.formatMessage({ id: 'charter.field.emailPlatform.placeholder' })}
                                options={[{ value: 'Klaviyo' }, { value: 'Mailchimp' }, { value: 'HubSpot' }, { value: 'Insider' }, { value: 'Other' }, { value: 'Not sure' }]}
                                filterOption={(input, option) => (option?.value as string).toLowerCase().includes(input.toLowerCase())}
                            />
                        </Form.Item>
                        <Form.Item
                            name="customerCountBand"
                            label={intl.formatMessage({ id: 'charter.field.customerCountBand' })}
                            rules={[{ required: true, message: intl.formatMessage({ id: 'charter.field.customerCountBand.required' }) }]}
                        >
                            <Select
                                size="large"
                                placeholder={intl.formatMessage({ id: 'charter.field.band.placeholder' })}
                                options={[
                                    { value: 'under_500', label: intl.formatMessage({ id: 'charter.band.under500' }) },
                                    { value: '500_2000', label: intl.formatMessage({ id: 'charter.band.500to2000' }) },
                                    { value: '2000_10000', label: intl.formatMessage({ id: 'charter.band.2000to10000' }) },
                                    { value: '10000_plus', label: intl.formatMessage({ id: 'charter.band.10000plus' }) },
                                ]}
                            />
                        </Form.Item>
                        <Button type="primary" block size="large" style={primaryButtonStyle} onClick={goNext}>
                            {intl.formatMessage({ id: 'charter.step.continue' })}
                        </Button>
                    </div>
                )}

                {step === STEP_LIST && (
                    <div>
                        {stepHeader('charter.step.list.title', 'charter.step.list.description')}
                        {/* The question that follows the purchase count. How many
                            have bought and how many can be emailed are different
                            numbers, and the second decides whether a survey has
                            anybody to go to at all. */}
                        <Form.Item
                            name="reachableListBand"
                            label={intl.formatMessage({ id: 'charter.field.reachableListBand' })}
                            rules={[{ required: true, message: intl.formatMessage({ id: 'charter.field.reachableListBand.required' }) }]}
                        >
                            <Select
                                size="large"
                                placeholder={intl.formatMessage({ id: 'charter.field.band.placeholder' })}
                                options={[
                                    { value: 'none', label: intl.formatMessage({ id: 'charter.band.none' }) },
                                    { value: 'under_500', label: intl.formatMessage({ id: 'charter.band.under500' }) },
                                    { value: '500_2000', label: intl.formatMessage({ id: 'charter.band.500to2000' }) },
                                    { value: '2000_10000', label: intl.formatMessage({ id: 'charter.band.2000to10000' }) },
                                    { value: '10000_plus', label: intl.formatMessage({ id: 'charter.band.10000plus' }) },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item
                            name="canSendWithin30Days"
                            label={intl.formatMessage({ id: 'charter.field.canSendWithin30Days' })}
                            rules={[{ required: true, message: intl.formatMessage({ id: 'charter.field.canSendWithin30Days.required' }) }]}
                        >
                            <Select
                                size="large"
                                placeholder={intl.formatMessage({ id: 'charter.field.canSendWithin30Days.placeholder' })}
                                options={[
                                    { value: 'yes', label: intl.formatMessage({ id: 'charter.send.yes' }) },
                                    { value: 'yes_with_signoff', label: intl.formatMessage({ id: 'charter.send.yesWithSignoff' }) },
                                    { value: 'no', label: intl.formatMessage({ id: 'charter.send.no' }) },
                                ]}
                            />
                        </Form.Item>
                        <Button type="primary" block size="large" style={primaryButtonStyle} onClick={goNext}>
                            {intl.formatMessage({ id: 'charter.step.continue' })}
                        </Button>
                    </div>
                )}

                {step === STEP_QUESTION && (
                    <div>
                        {stepHeader('charter.step.question.title', 'charter.step.question.description')}
                        {/* Alone on its own screen because it is the field that
                            qualifies the lead and doubles as messaging research.
                            No character cap, and room to actually write. */}
                        <Form.Item
                            name="unansweredQuestion"
                            rules={[
                                { required: true, message: intl.formatMessage({ id: 'charter.field.unansweredQuestion.required' }) },
                                { min: 10, message: intl.formatMessage({ id: 'charter.field.unansweredQuestion.tooShort' }) },
                            ]}
                        >
                            <TextArea
                                // maxRows matters more than minRows here. This is
                                // the field people actually write in, and without a
                                // ceiling a long answer grows the page and pushes
                                // the submit button off screen mid-sentence.
                                autoSize={{ minRows: isMobile ? 4 : 5, maxRows: isMobile ? 6 : 6 }}
                                style={{ borderRadius: '12px', border: '2px solid #e5e7eb', fontSize: '16px' }}
                                placeholder={intl.formatMessage({ id: 'charter.field.unansweredQuestion.placeholder' })}
                                autoFocus
                            />
                        </Form.Item>
                        <Button type="primary" block size="large" style={primaryButtonStyle} onClick={goNext}>
                            {intl.formatMessage({ id: 'charter.step.continue' })}
                        </Button>
                    </div>
                )}

                {step === STEP_ABOUT && (
                    <div>
                        {stepHeader('charter.step.about.title', 'charter.step.about.description')}
                        <Form.Item
                            name="contactName"
                            label={intl.formatMessage({ id: 'charter.field.contactName' })}
                            rules={[{ required: true, message: intl.formatMessage({ id: 'charter.field.contactName.required' }) }]}
                        >
                            <Input prefix={<UserOutlined style={{ color: '#9ca3af' }} />} style={inputStyle} autoFocus />
                        </Form.Item>
                        <Form.Item
                            name="email"
                            label={intl.formatMessage({ id: 'charter.field.email' })}
                            rules={[
                                { required: true, message: intl.formatMessage({ id: 'charter.field.email.required' }) },
                                { type: 'email', message: intl.formatMessage({ id: 'charter.field.email.invalid' }) },
                            ]}
                        >
                            <Input prefix={<MailOutlined style={{ color: '#9ca3af' }} />} style={inputStyle} />
                        </Form.Item>
                        <Button type="primary" block size="large" style={primaryButtonStyle} onClick={goNext}>
                            {intl.formatMessage({ id: 'charter.step.continue' })}
                        </Button>
                    </div>
                )}

                {step === STEP_ROLE && (
                    <div>
                        {stepHeader('charter.step.role.title', 'charter.step.role.description')}
                        <Form.Item
                            name="roleAtBrand"
                            rules={[{ required: true, message: intl.formatMessage({ id: 'charter.field.roleAtBrand.required' }) }]}
                        >
                            <Input style={inputStyle} placeholder={intl.formatMessage({ id: 'charter.field.roleAtBrand.placeholder' })} autoFocus />
                        </Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            size="large"
                            loading={submitResult.isLoading}
                            style={primaryButtonStyle}
                        >
                            {intl.formatMessage({ id: 'charter.form.submit' })}
                        </Button>
                    </div>
                )}
            </Form>

            {/* Both secondary actions on one row: vertical space in this card is
                scarce, and neither earns a line of its own. */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: '0.6rem', flexWrap: 'wrap' }}>
                {step > STEP_TERMS && (
                    <Button type="link" size="small" onClick={() => setTermsOpen(true)} style={{ color: '#6b7280', fontSize: 13, padding: 0, height: 'auto' }}>
                        {intl.formatMessage({ id: 'charter.step.reviewTerms' })}
                    </Button>
                )}
                <Text type="secondary" style={{ fontSize: 13 }}>
                    {intl.formatMessage({ id: 'charter.confirmation.existingAccount' })}{' '}
                    <Link to="/sign-in">{intl.formatMessage({ id: 'charter.confirmation.signIn' })}</Link>
                </Text>
            </div>

            <Modal
                open={termsOpen}
                onCancel={() => setTermsOpen(false)}
                footer={null}
                title={intl.formatMessage({ id: 'charter.step.reviewTerms' })}
                styles={{ body: { textAlign: 'left' } }}
            >
                <div className="charter-markdown">
                    <ReactMarkdown>{charterConfig?.termsMarkdown || ''}</ReactMarkdown>
                </div>
            </Modal>
        </ModernAuthLayout>
    );
};

export default CharterPage;
